"use client";

import { SHANI_TRANSIT_HEADLINE } from "@/lib/saturn-status";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { getCurrentTransits } from "@/lib/api/transits";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { getTodayISTDateString } from "@/lib/ist-date";
import { getBrowserQueryClient } from "@/lib/query-client";
import { SaturnPositionCard, RashiPicker } from "./_sections";

export default function ShaniTransitClient({
  crossLinks,
  resultOffer,
}: {
  crossLinks: ReactNode;
  resultOffer: ReactNode;
}) {
  const dateStr = useMemo(() => getTodayISTDateString(), []);
  const [selectedRashi, setSelectedRashi] = useState<string | null>(null);
  const completionTrackedRef = useRef(false);
  const queryRashi = selectedRashi ?? "mesh";

  const { data: transitData, isLoading } = useQuery(
    {
      queryKey: ["transits", "current", queryRashi, dateStr],
      queryFn: () => getCurrentTransits(queryRashi, dateStr),
      staleTime: 30 * 60 * 1000,
      retry: 1,
    },
    getBrowserQueryClient()
  );

  const saturnTransit = transitData?.active_transits.find((entry) => entry.planet_id === "saturn");
  const selectedStatus =
    selectedRashi && transitData?.moon_rashi === selectedRashi ? transitData.sade_sati : undefined;

  useEffect(() => {
    if (!transitData || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackCalculatorUsed({ calculator: "shani-transit", source_path: "/shani-transit" });
  }, [transitData]);

  return (
    <div className="mx-auto min-h-[calc(100vh-130px)] max-w-2xl px-4 py-10 sm:px-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-5xl mb-3">♄</div>
        <h1
          className="text-2xl sm:text-3xl font-bold mb-2"
          style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
        >
          {SHANI_TRANSIT_HEADLINE}
        </h1>
        <p className="text-body" style={{ color: "var(--ish-text-muted)" }}>
          Saturn&apos;s current position and which Moon signs are under Sade Sati
        </p>
      </div>

      <SaturnPositionCard saturnTransit={saturnTransit ?? null} calculationDate={dateStr} />
      <RashiPicker
        selectedRashi={selectedRashi}
        onSelect={setSelectedRashi}
        sadeSati={selectedStatus}
        saturnTransit={saturnTransit ?? null}
        isLoading={isLoading}
      />

      {transitData && resultOffer}

      {crossLinks}
    </div>
  );
}
